import React, { useMemo, useState, useEffect } from 'react';
import classnames from 'classnames';
import { getLocale, useIntl } from 'umi';
import { useUnmount } from 'ahooks';
import { Icon, notification } from 'tea-component';
import Abnormal from '@intelligence/components/Abnormal';
import NavigatorToolbar from '@gp/components/COSDocPreviewer/components/NavigatorToolbar';
import usePDFUrlEffect from '@intelligence/components/PDFPreviewDownload/_hooks/usePDFUrlEffect';
import useCOSDocPreview from '@intelligence/components/PDFPreviewDownload/_hooks/useCOSDocPreview';
import useHTMLReportPreview from '@intelligence/components/PDFPreviewDownload/_hooks/useHTMLReportPreview';
import EmbedPreferenceIframe from '@intelligence/components/EmbedPreferenceIframe';
import { downloadFile } from '@intelligence/utils/commonUtils';
import { buriedBtnPoint, btnPointInter } from '@home/utils/buriedPoint';
import { dbiBtnLogMes } from '@intelligence/utils/buriedPointCfg';
import { getIsHTMLReportPath, getIsZipReportPath } from '@home/utils/reportPreview';
import { getIsPDFFile, navigateBack } from './utils';
import {
  pageReportTypeConfig,
  findReportObjectBySystemId,
  NEW_PDF_PREVIEW_STYLE_REPORTS,
} from '@intelligence/_config/reportConfig';

import type { IPDFPreviewDownloadProps } from '@intelligence/components/PDFPreviewDownload/types';

import '@intelligence/components/PDFPreviewDownload/index.scss';

import NoPermissionImg from '@intelligence/assets/imgs/common/no-permission.png';

const PDFPreviewDownload: React.FC<IPDFPreviewDownloadProps> = (props) => {
  const {
    style,
    className,
    systemId,
    studioId,
    resourceId,
    referer,
    fileName,
    filePath,
    originalFilePath = '',
    fileExtName = '',
    downloadable = true,
    downloadLogKey,
    watermarkEnable,
    iicLoading,
    fetchPDFUrl,
    aside,
    footer,
    lang,
  } = props;

  // 是否新样式展示
  const isNewStyleReport = NEW_PDF_PREVIEW_STYLE_REPORTS.includes(systemId);
  const isStudioResourceReport = systemId === pageReportTypeConfig.studioReport.systemIdCur;
  // 情报只根据 COS path 判断类型，fileExtName 仅专区提前预览使用
  const previewPath = isStudioResourceReport ? (filePath || fileExtName) : filePath;
  const isPDFPreviewMode = isStudioResourceReport
    ? getIsPDFFile(filePath)
      && !getIsHTMLReportPath(previewPath)
      && !getIsZipReportPath(previewPath)
    : getIsPDFFile(filePath);
  const isHTMLPreviewMode = isStudioResourceReport
    ? getIsHTMLReportPath(previewPath) || getIsZipReportPath(previewPath)
    : getIsHTMLReportPath(filePath);
  const isZipPreviewMode = isStudioResourceReport
    ? getIsZipReportPath(previewPath) && !isHTMLPreviewMode
    : getIsZipReportPath(filePath);
  const isHTMLZipOriginalFile = getIsHTMLReportPath(originalFilePath) || getIsZipReportPath(originalFilePath);
  /**
   * 是否为COS预览模式
   * 存储在广州COS桶的报告（并且不是PDF/HTML/ZIP文件）走COS预览，其余的报告不走COS预览
   */
  const isCOSPreviewMode = !isPDFPreviewMode && !isHTMLPreviewMode && !isZipPreviewMode;

  const locale = getLocale();

  const { formatMessage } = useIntl();

  // 是否报告加载中
  const [isLoading, setIsLoading] = useState<boolean>(true);
  // 是否加载异常
  const [isError, setIsError] = useState(false);
  // 是否报告被删除
  const [isDeleted, setIsDeleted] = useState(false);
  // 是否报告下载中
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  // 报告文件后缀
  const [fileSuffix, setFileSuffix] = useState('');
  // 报告文件原始访问连接（可用于访问原始文件、下载）
  const [url, setUrl] = useState<string>('');
  // 报告文件访问连接（经过处理的，仅用于预览，不能用于下载）
  const [previewUrl, setPreviewUrl] = useState<string>('');

  /**
   * 纯PDF预览模式
   * 前端下载PDF文件流，利用浏览器PDF预览能力实现预览（仅支持预览PDF）
   */
  const { onReload: handleReloadPDF } = usePDFUrlEffect({
    executable: isPDFPreviewMode,
    systemId,
    studioId,
    resourceId,
    fetchPDFUrl,
    setIsLoading,
    setIsError,
    setFileSuffix,
    setUrl,
    setPreviewUrl,
    onDeleted: () => {
      setIsDeleted(true);
      setIsLoading(false);
    },
  });

  /**
   * COS预览模式，利用腾讯云COS能力实现预览功能（包含多种文件PDF、ppt）
   */
  const { updateDownloadUrl: updateDownloadUrlCOS, onReload: handleReloadCOS } = useCOSDocPreview({
    executable: isCOSPreviewMode,
    systemId,
    studioId,
    resourceId,
    watermarkEnable,
    fetchPDFUrl,
    cosDocKey: filePath,
    setIsLoading,
    setIsError,
    setFileSuffix,
    setUrl,
    setPreviewUrl,
    onDeleted: () => {
      setIsDeleted(true);
      setIsLoading(false);
    },
  });

  const {
    updateDownloadUrl: updateDownloadUrlHTML,
    updateOriginalFileDownloadUrl,
    onReload: handleReloadHTML,
  } = useHTMLReportPreview({
    executable: isHTMLPreviewMode,
    systemId,
    studioId,
    resourceId,
    fetchPDFUrl,
    reportFileKey: filePath,
    originalFileKey: originalFilePath,
    lang,
    setIsLoading,
    setIsError,
    setFileSuffix,
    setUrl,
    setPreviewUrl,
    onDeleted: () => {
      setIsDeleted(true);
      setIsLoading(false);
    },
  });

  useEffect(() => {
    if (!isZipPreviewMode) {
      return;
    }
    setPreviewUrl('');
    setIsLoading(false);
    setIsError(true);
  }, [isZipPreviewMode, filePath]);

  const handleReload = (e: React.SyntheticEvent<HTMLElement>) => {
    if (isHTMLPreviewMode) {
      handleReloadHTML(e);
      return;
    }
    if (isCOSPreviewMode) {
      handleReloadCOS(e);
      return;
    }
    handleReloadPDF(e);
  };

  // IIC 报告
  const isIicReportSystem = systemId === pageReportTypeConfig.insightsCenterLibrary.systemIdCur;
  // IIC 报告禁止下载
  const isIicDisabledDownload = isIicReportSystem && !iicLoading && !downloadable;

  const downloadLog = () => {
    // downloadLogKey 就是下载定义的打点iddownloadLogKeyCur; 兼容旧代码传过来的是字符，新代码传打点信息过来
    const downloadLogKeyIsString = typeof downloadLogKey === 'string';
    // @ts-ignore
    const downloadLogCfg: btnPointInter = downloadLogKeyIsString ? dbiBtnLogMes?.[downloadLogKey] : downloadLogKey;
    if (downloadLogCfg) {
      downloadLogCfg.extInfo = resourceId;
      buriedBtnPoint(downloadLogCfg);
    }
  };

  const notificationDownloadCommonError = () => {
    notification.error({
      unique: true,
      duration: 3000,
      description: formatMessage({ id: 'dbi_download_common_error_tips' }),
    });
  };

  const handleDownloadPDF = async () => {
    if (!url || !fileSuffix) {
      notificationDownloadCommonError();
      return;
    }
    downloadFile({ url, fileName: `${fileName}.${fileSuffix}` });
  };

  const handleDownloadCOS = async () => {
    if (!!url && !!fileSuffix) {
      handleDownloadPDF();
    } else {
      const { isError, url: urlTmp, suffix: suffixTmp } = await updateDownloadUrlCOS();
      if (isError || !urlTmp || !suffixTmp) {
        notificationDownloadCommonError();
        return;
      }
      downloadFile({ url: urlTmp, fileName: `${fileName}.${suffixTmp || 'pdf'}` });
    }
  };

  const handleDownloadHTML = async () => {
    if (isHTMLZipOriginalFile) {
      const { isError, url: urlTmp, suffix: suffixTmp } = await updateOriginalFileDownloadUrl();
      if (isError || !urlTmp || !suffixTmp) {
        notificationDownloadCommonError();
        return;
      }
      downloadFile({ url: urlTmp, fileName: `${fileName}.${suffixTmp}` });
      return;
    }
    if (!!url && !!fileSuffix) {
      handleDownloadPDF();
      return;
    }
    const { isError, url: urlTmp, suffix: suffixTmp } = await updateDownloadUrlHTML();
    if (isError || !urlTmp || !suffixTmp) {
      notificationDownloadCommonError();
      return;
    }
    downloadFile({ url: urlTmp, fileName: `${fileName}.${suffixTmp || 'html'}` });
  };

  const handleDownload = async () => {
    if (!downloadable) return;
    if (isLoading) return;
    if (isDownloading) return;
    if (isError) return;

    try {
      setIsDownloading(true);
      if (isPDFPreviewMode) {
        await handleDownloadPDF();
        return;
      }
      if (isHTMLPreviewMode) {
        await handleDownloadHTML();
        return;
      }
      await handleDownloadCOS();
    } catch (e) {
      console.error(e);
    } finally {
      downloadLog();
      setIsDownloading(false);
    }
  };

  const BtnLogMesCur: btnPointInter = useMemo(() => {
    // 如果systemId已经写到配置了，就从配置中取
    const reportObject = findReportObjectBySystemId(pageReportTypeConfig, systemId);
    if (reportObject?.systemIdCur === systemId && reportObject?.BtnLogMesCur) {
      return reportObject.BtnLogMesCur;
    }
    switch (systemId) {
      case 'intelligence': // MarketReportsDetail报告
        return dbiBtnLogMes.MarketReportsDetail;
      case 'intelligence_device':
        return dbiBtnLogMes.mobileDeviceReportsDetail;
      default:
        return dbiBtnLogMes.gameAnalysisReportsDetail;
    }
  }, [systemId]);

  // 打开报告，添加报告打点
  useEffect(() => {
    try {
      const logMes = { ...BtnLogMesCur };
      logMes.extInfo = resourceId;
      buriedBtnPoint(logMes);
    } catch (err) {
      console.error('BtnLogMesCur报告打点未添加:', err);
    }
  }, [BtnLogMesCur]);

  useUnmount(() => {
    if (!!url) {
      URL.revokeObjectURL(url);
    }

    if (!!previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
  });

  return (
    <div
      style={style}
      className={classnames('dbi-pdf-preview-download-page', className, {
        // 点赞分享按钮在底部样式
        'has-footer': footer,
        // 点赞分享按钮在右侧样式
        'has-aside': isNewStyleReport,
      })}
    >
      <div className="dbi-pdf-preview-download-page__header">
        <div
          className={classnames('dbi-pdf-preview-download-page__header__left', {
            'dbi-pdf-preview-download-page__header__left-small': isIicDisabledDownload,
          })}
        >
          <NavigatorToolbar
            showBackIcon={!!referer}
            title={fileName}
            onNavigatorClick={() => {
              navigateBack(referer);
            }}
          />
        </div>
        {/* IIC 报告无法下载，显示对应文案 */}
        {isIicDisabledDownload ? (
          <div className="dbi-pdf-preview-download-page__header__info">
            {formatMessage(
              { id: 'dbi_insight_center_library_report_unavailable_download' },
              {
                email: (
                  <span className="dbi-pdf-preview-download-page__header__info__contact">
                    {locale === 'en-US' ? 'huidonghe@tencent.com' : 'huidonghe'}
                  </span>
                ),
              },
            )}
          </div>
        ) : (
          /* 正常显示下载按钮 */
          <div>
            {downloadable && (
              <Icon
                className={classnames('dbi-pdf-preview-download-page__header__download-btn', {
                  disabled: isLoading || isDownloading || iicLoading || isError || isDeleted,
                })}
                type={isDownloading ? 'loading' : 'download'}
                // eslint-disable-next-line @typescript-eslint/no-misused-promises
                onClick={handleDownload}
              />
            )}
          </div>
        )}
      </div>
      <div className="dbi-pdf-preview-download-page__preview-container">
        <div
          className={classnames('dbi-pdf-preview-download-page__preview-container__inner', {
            // 新展示样式取消上下填充
            'no-vertical-padding': isNewStyleReport,
          })}
        >
          <Abnormal
            className="dbi-pdf-preview-download-page__preview-container__inner__abnormal"
            isLoading={isLoading}
            component={
              <>
                {(isError || isDeleted) && (
                  <div className="dbi-pdf-preview-download-page__preview-iframe dbi-pdf-preview-download-page__preview-iframe--error">
                    <div className="dbi-pdf-preview-download-page__preview-iframe__error-box">
                      <img
                        className="dbi-pdf-preview-download-page__preview-iframe__error-box__img"
                        src={NoPermissionImg}
                        alt=""
                      />
                      <p className="dbi-pdf-preview-download-page__preview-iframe__error-box__text">
                        {formatMessage({
                          id: isDeleted ? 'dbi_insight_report_deleted_tips' : 'dbi_insight_report_load_failure_tips',
                        })}
                      </p>
                      {isError && !isDeleted && !isZipPreviewMode && (
                        <p
                          className="dbi-pdf-preview-download-page__preview-iframe__error-box__text--btn"
                          onClick={handleReload}
                        >
                          {formatMessage({ id: 'dbi_insight_report_reload_tips' })}
                        </p>
                      )}
                    </div>
                  </div>
                )}
                {!isError && !isDeleted && (
                  <div className="dbi-pdf-preview-download-page__preview-iframe">
                    {isHTMLPreviewMode && (
                      <EmbedPreferenceIframe
                        className="dbi-pdf-preview-download-page__preview-iframe__iframe-node"
                        url={previewUrl}
                        useWatermark={watermarkEnable !== '0'}
                      />
                    )}
                    {!isHTMLPreviewMode && (
                      <iframe className="dbi-pdf-preview-download-page__preview-iframe__iframe-node" src={previewUrl} />
                    )}
                  </div>
                )}
              </>
            }
          />
        </div>
      </div>
      {!isLoading && !isError && !isDeleted && footer && (
        <div className="dbi-pdf-preview-download-page__footer">{footer}</div>
      )}
      {!isLoading && !isError && !isDeleted && aside && (
        <div className="dbi-pdf-preview-download-page__sider">{aside}</div>
      )}
    </div>
  );
};

export default PDFPreviewDownload;
